import { useState } from 'react';
import {
    // Cpu,
    Clock,
    Users,
    Settings,
    // Phone,
    CheckCircle,
    MapPin
} from 'lucide-react';
import { EquiposTable } from './EquiposTable';
import Icon from '../../components/Icon';
import { useDevice } from '../../hooks/useDevices';
// import { useLeadDetail } from '../../hooks/useLeadsDetail';
import { useLead } from '../../hooks/useLeads';

interface ModeInfo {
    code: string;
    name: string;
    email: string;
    flag: string;
    currency: string;
    timezone: string;
    language: string;
    population: string;
    capital: string;
    features: string[];
    advantages: string[];
    color: string;
}

const modeData: Record<string, ModeInfo> = {
    EC: {
        code: 'EC',
        name: 'Ecuador',
        email: 'r490_centralec@pas-hq.dev',
        flag: '🇪🇨',
        currency: 'Pashq123*',
        timezone: 'UTC-5',
        language: 'Español',
        population: '17.8M',
        capital: 'Quito',
        features: [
            'Dolarización oficial',
            'Zona horaria estable'
        ],
        advantages: [
            'Estabilidad monetaria',
            'Ubicación estratégica'
        ],
        color: 'blue'
    },
    MX: {
        code: 'MX',
        name: 'México',
        email: 'r490_centralmx@pas-hq.dev',
        flag: '🇲🇽',
        currency: 'Pashq123*',
        timezone: 'UTC-6',
        language: 'Español',
        population: '128M',
        capital: 'Ciudad de México',
        features: [
            'Economía diversificada',
            'USMCA/T-MEC'
        ],
        advantages: [
            'Mercado interno grande',
            'Acceso a EEUU/Canadá'
        ],
        color: 'green'
    }
};

const Configuracion = () => {
    const [activeSection, setActiveSection] = useState('equipos');
    // const [isDirty, setIsDirty] = useState(false);
    const [saved, setSaved] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(5);
    // const [searchTerm, setSearchTerm] = useState('');

    const [showDeviceForm, setShowDeviceForm] = useState(false);
    const [selectedMode, setSelectedMode] = useState<'EC' | 'MX'>('EC');
    
    // Estados para gestión de usuarios
    const [showUserForm, setShowUserForm] = useState(false);
    const [editingUser, setEditingUser] = useState<any>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [currentUserPage, setCurrentUserPage] = useState(1);
    const [usersPerPage] = useState(10);
    
    // Formulario de usuario
    const [userForm, setUserForm] = useState({
        fullName: '',
        email: '',
        phone: '',
        department: '',
        role: '',
        status: 'active',
        password: '',
        confirmPassword: '',
        permissions: [] as string[]
    });
    
    // Lista de permisos disponibles
    const permissions = [
        { id: 'dashboard.view', name: 'Ver Dashboard' },
        { id: 'leads.view', name: 'Ver Leads' },
        { id: 'leads.create', name: 'Crear Leads' },
        { id: 'leads.edit', name: 'Editar Leads' },
        { id: 'leads.delete', name: 'Eliminar Leads' },
        { id: 'deals.view', name: 'Ver Deals' },
        { id: 'deals.create', name: 'Crear Deals' },
        { id: 'deals.edit', name: 'Editar Deals' },
        { id: 'deals.delete', name: 'Eliminar Deals' },
        { id: 'contacts.view', name: 'Ver Contactos' },
        { id: 'contacts.create', name: 'Crear Contactos' },
        { id: 'contacts.edit', name: 'Editar Contactos' },
        { id: 'contacts.delete', name: 'Eliminar Contactos' },
        { id: 'users.view', name: 'Ver Usuarios' },
        { id: 'users.create', name: 'Crear Usuarios' },
        { id: 'users.edit', name: 'Editar Usuarios' },
        { id: 'users.delete', name: 'Eliminar Usuarios' },
        { id: 'reports.view', name: 'Ver Reportes' },
        { id: 'reports.export', name: 'Exportar Reportes' },
        { id: 'configuracion.view', name: 'Ver Configuración' },
        { id: 'configuracion.edit', name: 'Editar Configuración' }
    ];
    
    // Datos mock de usuarios para demostración
    const [users, setUsers] = useState([
        {
            id: 1,
            fullName: 'Coordinador',
            email: 'coordinador_iaen@4clients.io',
            phone: '+593 99 123 4567',
            department: 'ventas',
            role: 'admin',
            status: 'active',
            lastLogin: '2024-01-15 14:30',
            permissions: ['dashboard.view', 'leads.view', 'leads.create', 'leads.edit']
        },
        {
            id: 2,
            fullName: 'Agente 1 Apoyo',
            email: 'agente1_iaen@4clients.io',
            phone: '+593 98 765 4321',
            department: 'marketing',
            role: 'user',
            status: 'active',
            lastLogin: '2024-01-15 10:15',
            permissions: ['dashboard.view', 'leads.view', 'deals.view']
        },
        {
            id: 3,
            fullName: 'Soporte',
            email: 'dev@4clients.io',
            phone: '+593 97 456 7890',
            department: 'soporte',
            role: 'superadmin',
            status: 'active',
            lastLogin: '2024-01-15 16:45',
            permissions: ['dashboard.view', 'leads.view', 'leads.create', 'leads.edit', 'leads.delete', 'users.view', 'users.create', 'users.edit', 'users.delete']
        }
    ]);

    // Configuraciones por sección
    const [ configs, setConfigs ] = useState({
        equipos: {
            nombre: 'Terminal-001',
            tipo: 'biometrico',
            ubicacion: 'Entrada Principal',
            estado: 'activo',
            firmware: '2.1.3'
        },
        redes: {
            ip: '192.168.1.100',
            mascara: '255.255.255.0',
            gateway: '192.168.1.1',
            dns1: '8.8.8.8',
            dns2: '8.8.4.4',
            dhcp: false
        },
        seguridad: {
            timeout: 30,
            intentos: 3,
            bloqueo: 300,
            encriptacion: true,
            audit_log: true
        },
        almacenamiento: {
            capacidad_total: '32GB',
            capacidad_usada: '12GB',
            backup_auto: true,
            retencion: 90,
            compresion: true
        },
        sincronizacion: {
            servidor: 'servidor.empresa.com',
            puerto: 8080,
            ssl: true,
            intervalo: 15,
            ultimo_sync: '2025-06-13 09:30:00'
        },
        usuarios: {
            max_usuarios: 5000,
            usuarios_activos: 1247,
            registro_auto: false,
            verificacion_dual: true,
            plantillas_multiples: true
        }
    });

    const menuItems = [
        { id: 'equipos', label: 'Procesos', icon: Settings, color: 'blue' },
        // { id: 'centro_atencion', label: 'Status', icon: Settings, color: 'orange' },
        // { id: 'llamadas', label: 'Fuentes', icon: Settings, color: 'indigo' },
        { id: 'usuarios_firebase', label: 'Gestión de Usuarios', icon: Users, color: 'indigo' }
    ];

    const handleOpenForm = () => {
        setShowDeviceForm(prev => !prev);
    };

    // Funciones para gestión de usuarios
    const handleSaveUser = () => {
        if (!userForm.fullName || !userForm.email || !userForm.role) {
            alert('Por favor completa todos los campos obligatorios');
            return;
        }

        if (!editingUser && (!userForm.password || !userForm.confirmPassword)) {
            alert('Por favor ingresa la contraseña');
            return;
        }

        if (!editingUser && userForm.password !== userForm.confirmPassword) {
            alert('Las contraseñas no coinciden');
            return;
        }

        if (editingUser) {
            // Actualizar usuario existente
            setUsers(users.map(user => 
                user.id === editingUser.id 
                    ? { ...user, ...userForm, id: editingUser.id }
                    : user
            ));
        } else {
            // Crear nuevo usuario
            const newUser = {
                ...userForm,
                id: Math.max(...users.map(u => u.id)) + 1,
                lastLogin: 'Nunca'
            };
            setUsers([...users, newUser]);
        }

        // Limpiar formulario
        setUserForm({
            fullName: '',
            email: '',
            phone: '',
            department: '',
            role: '',
            status: 'active',
            password: '',
            confirmPassword: '',
            permissions: []
        });
        setShowUserForm(false);
        setEditingUser(null);
    };

    const handleEditUser = (user: any) => {
        setEditingUser(user);
        setUserForm({
            fullName: user.fullName,
            email: user.email,
            phone: user.phone || '',
            department: user.department || '',
            role: user.role,
            status: user.status,
            password: '',
            confirmPassword: '',
            permissions: user.permissions || []
        });
        setShowUserForm(true);
    };

    const handleDeleteUser = (userId: number) => {
        if (window.confirm('¿Estás seguro de que quieres eliminar este usuario?')) {
            setUsers(users.filter(user => user.id !== userId));
        }
    };

    const handleToggleUserStatus = (userId: number) => {
        setUsers(users.map(user => 
            user.id === userId 
                ? { ...user, status: user.status === 'active' ? 'inactive' : 'active' }
                : user
        ));
    };

    // Filtrar usuarios
    const filteredUsers = users.filter(user => {
        const matchesSearch = user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             user.email.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRole = !filterRole || user.role === filterRole;
        return matchesSearch && matchesRole;
    });

    const handleInputChange = (
        section: keyof typeof configs,
        field: string,
        value: string | number | boolean
    ) => {
        setConfigs(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
        // setIsDirty(true);
        setSaved(false);
    };

    // const handleSave = () => {
    //     // Simular guardado
    //     setTimeout(() => {
    //         // setIsDirty(false);
    //         setSaved(true);
    //         setTimeout(() => setSaved(false), 3000);
    //     }, 1000);
    // };

    // const handleReset = () => {
    //     if (window.confirm('¿Estás seguro de que quieres restablecer la configuración?')) {
    //         // Aquí resetearías a valores por defecto
    //         setIsDirty(false);
    //     }
    // };
    const {processes} = useLead({
        page: currentPage,
        pageSize});


    const { data: equipos, loading} = useDevice();
    console.log(equipos);
    

    
    // const {
    //     data: equipos,

    //     loading,
    //     error,
    //     total,
    //     totalPages,
    //     refetch
    // } = useEquipos({
    //     page: currentPage,
    //     pageSize,
    //     search: debouncedSearch,
    //     ...filters
    // });

    const renderEquiposConfig = () => (
        <div className="space-y-4">

            {showDeviceForm && (
                <div className="bg-gray-50 dark:bg-gray-800  border border-gray-300 dark:border-gray-700 p-3 rounded-lg grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Nombre del Equipo
                        </label>
                        <input
                            type="text"
                            value={configs.equipos.nombre}
                            onChange={(e) => handleInputChange('equipos', 'nombre', e.target.value)}
                            className="w-full px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Tipo de Equipo
                        </label>
                        <select
                            value={configs.equipos.tipo}
                            onChange={(e) => handleInputChange('equipos', 'tipo', e.target.value)}
                            className="w-full px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            <option value="biometrico">Facial</option>
                            <option value="tarjeta">Puerta</option>
                            <option value="hibrido">Cámara</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Dirección IP
                        </label>
                        <input
                            type="text"
                            value={configs.equipos.ubicacion}
                            onChange={(e) => handleInputChange('equipos', 'ubicacion', e.target.value)}
                            className="w-full px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Estado
                        </label>
                        <select
                            value={configs.equipos.estado}
                            onChange={(e) => handleInputChange('equipos', 'estado', e.target.value)}
                            className="w-full px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            <option value="activo">Activo</option>
                            <option value="inactivo">Inactivo</option>
                            <option value="mantenimiento">Mantenimiento</option>
                        </select>
                    </div>
                </div>
            )}
            <div className='flex flex-row gap-2'>
                {showDeviceForm && (
                    <button
                        className={`flex items-center px-3 py-2 text-xs text-white rounded-md font-medium transition-colors bg-primary hover:bg-amber-400  `}
                    >

                        <Icon name='plus' className='w-4 h-4 mr-2' />
                        Añadir
                    </button>
                )}
                <button
                    onClick={handleOpenForm}
                    className={`flex items-center px-3 py-2 text-xs text-white rounded-md font-medium transition-colors duration-300 ${showDeviceForm ? "bg-red-500 hover:bg-red-400" : "bg-primary hover:bg-amber-400"} `}
                >
                    {showDeviceForm ? (
                        <Icon name='close' className='w-4 h-4 mr-2' />
                    ) : (
                        <Icon name='plus' className='w-4 h-4 mr-2' />
                    )}

                    {showDeviceForm ? "Cancelar" : "Añadir proceso"}
                </button>
            </div>

            <EquiposTable
                equipos={processes}
                loading={loading}
                currentPage={currentPage}
                // totalPages={totalPages}
                pageSize={pageSize}
                // total={total}
                onPageChange={setCurrentPage}
                // onRefresh={refetch}
            />

            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Información del Sistema</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    Firmware: {configs.equipos.firmware} | Última actualizacion: 2025-06-25 14:18:22
                </p>
            </div>
        </div>
    );

    const renderCentroConfig = () => {


        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <div className=" mx-auto">

                    {/* Toggle selector */}
                    <div className="flex justify-center mb-10">
                        <div className="relative bg-gray-200 rounded-full p-0 shadow-inner">
                            <div className="flex relative">
                                {/* Slider animado */}
                                <div
                                    className={`absolute top-1 bottom-1 rounded-full transition-all duration-300 ease-in-out shadow-lg`}
                                    style={{
                                        width: 'calc(50% - 4px)',
                                    }}
                                />

                                {/* Opción Ecuador */}
                                <button
                                    className={`relative z-10 flex items-center gap-3 px-8 py-2 rounded-full font-semibold transition-all duration-300`}
                                    style={{ width: '200px' }}
                                >
                                    <span className="text-2xl">{modeData.EC.flag}</span>
                                    <div className="text-left">
                                        <div className="text-lg font-bold">{modeData.EC.code}</div>
                                        <div className="text-xs opacity-90">{modeData.EC.name}</div>
                                    </div>
                                </button>

                                {/* Opción México */}
                                <button
                                    className={`relative z-10 flex items-center gap-3 px-8 py-2 rounded-full font-semibold transition-all duration-300`}
                                    style={{ width: '200px' }}
                                >
                                    <span className="text-2xl">{modeData.MX.flag}</span>
                                    <div className="text-left">
                                        <div className="text-lg font-bold">{modeData.MX.code}</div>
                                        <div className="text-xs opacity-90">{modeData.MX.name}</div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>


                    {/* Información en dos columnas */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Card Ecuador */}
                        <div
                            onClick={() => setSelectedMode('EC')}
                        >
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <span className="text-4xl">{modeData.EC.flag}</span>
                                    <div>
                                        <h2 className="text-2xl font-bold text-gray-900">{modeData.EC.name}</h2>
                                        <p className="text-blue-600 font-medium">{modeData.EC.code}</p>
                                    </div>
                                </div>
                                {selectedMode === 'EC' && (
                                    <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                        Seleccionado
                                    </div>
                                )}
                            </div>

                            {/* Información básica */}
                            <div className="grid grid-cols-1 gap-4 mb-6">
                                <div className="flex items-center gap-2 text-gray-600">
                                    <MapPin className="w-4 h-4" />
                                    <span className="text-sm">{modeData.EC.capital}</span>
                                </div>
                                <div className="flex items-center gap-2 text-gray-600">
                                    <Clock className="w-4 h-4" />
                                    <span className="text-sm">{modeData.EC.timezone}</span>
                                </div>
                            </div>
                        </div>

                        {/* Card México */}
                        <div
                            onClick={() => setSelectedMode('MX')}
                        >
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <span className="text-4xl">{modeData.MX.flag}</span>
                                    <div>
                                        <h2 className="text-2xl font-bold text-gray-900">{modeData.MX.name}</h2>
                                        <p className="text-green-600 font-medium">{modeData.MX.code}</p>
                                    </div>
                                </div>
                                {selectedMode === 'MX' && (
                                    <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                                        Seleccionado
                                    </div>
                                )}
                            </div>

                            {/* Información básica */}
                            <div className="grid grid-cols-1 gap-4 mb-6">
                                <div className="flex items-center gap-2 text-gray-600">
                                    <MapPin className="w-4 h-4" />
                                    <span className="text-sm">{modeData.MX.capital}</span>
                                </div>
                                <div className="flex items-center gap-2 text-gray-600">
                                    <Clock className="w-4 h-4" />
                                    <span className="text-sm">{modeData.MX.timezone}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    };

    const renderUsuariosConfig = () => (
        <div className="space-y-6">
            {/* Header con botones de acción */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Gestión de Usuarios
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Administra usuarios, roles y permisos del sistema
                    </p>
                </div>
                
                <div className="flex flex-wrap gap-3">
                    <button
                        onClick={() => setShowUserForm(!showUserForm)}
                        className="flex items-center px-4 py-2 bg-primary hover:bg-amber-400 text-white rounded-lg transition-colors duration-200"
                    >
                        <Icon name="plus" className="w-4 h-4 mr-2" />
                        Nuevo Usuario
                    </button>
                    
                    <button className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200">
                        <Icon name="download" className="w-4 h-4 mr-2" />
                        Exportar
                    </button>
                    
                    <button className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200">
                        <Icon name="upload" className="w-4 h-4 mr-2" />
                        Importar
                    </button>
                </div>
            </div>

            {/* Formulario de creación/edición de usuario */}
            {showUserForm && (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                            {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
                        </h4>
                        <button
                            onClick={() => {
                                setShowUserForm(false);
                                setEditingUser(null);
                            }}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                            <Icon name="close" className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Información Personal */}
                        <div className="space-y-4">
                            <h5 className="font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                                Información Personal
                            </h5>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Nombre Completo *
                                </label>
                                <input
                                    type="text"
                                    value={userForm.fullName}
                                    onChange={(e) => setUserForm({...userForm, fullName: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                                    placeholder="Ej: Juan Pérez"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Email *
                                </label>
                                <input
                                    type="email"
                                    value={userForm.email}
                                    onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                                    placeholder="Ej: juan.perez@empresa.com"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Teléfono
                                </label>
                                <input
                                    type="tel"
                                    value={userForm.phone}
                                    onChange={(e) => setUserForm({...userForm, phone: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                                    placeholder="Ej: +593 99 123 4567"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Departamento
                                </label>
                                <select
                                    value={userForm.department}
                                    onChange={(e) => setUserForm({...userForm, department: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                                >
                                    <option value="">Seleccionar departamento</option>
                                    <option value="ventas">Ventas</option>
                                    <option value="marketing">Marketing</option>
                                    <option value="soporte">Soporte</option>
                                    <option value="administracion">Administración</option>
                                    <option value="desarrollo">Desarrollo</option>
                                </select>
                            </div>
                        </div>

                        {/* Información de Acceso */}
                        <div className="space-y-4">
                            <h5 className="font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                                Información de Acceso
                            </h5>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Rol *
                                </label>
                                <select
                                    value={userForm.role}
                                    onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                                >
                                    <option value="">Seleccionar rol</option>
                                    <option value="superadmin">Super Administrador</option>
                                    <option value="admin">Administrador</option>
                                    <option value="user">Usuario</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Estado
                                </label>
                                <select
                                    value={userForm.status}
                                    onChange={(e) => setUserForm({...userForm, status: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                                >
                                    <option value="active">Activo</option>
                                    <option value="inactive">Inactivo</option>
                                    <option value="suspended">Suspendido</option>
                                </select>
                            </div>

                            {!editingUser && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Contraseña *
                                        </label>
                                        <input
                                            type="password"
                                            value={userForm.password}
                                            onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                                            placeholder="Mínimo 8 caracteres"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Confirmar Contraseña *
                                        </label>
                                        <input
                                            type="password"
                                            value={userForm.confirmPassword}
                                            onChange={(e) => setUserForm({...userForm, confirmPassword: e.target.value})}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                                            placeholder="Repetir contraseña"
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Permisos Específicos */}
                    <div className="mt-6">
                        <h5 className="font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
                            Permisos Específicos
                        </h5>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {permissions.map((permission) => (
                                <label key={permission.id} className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={userForm.permissions.includes(permission.id)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setUserForm({
                                                    ...userForm,
                                                    permissions: [...userForm.permissions, permission.id]
                                                });
                                            } else {
                                                setUserForm({
                                                    ...userForm,
                                                    permissions: userForm.permissions.filter(p => p !== permission.id)
                                                });
                                            }
                                        }}
                                        className="rounded border-gray-300 text-primary focus:ring-primary"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                        {permission.name}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Botones de acción */}
                    <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <button
                            onClick={() => {
                                setShowUserForm(false);
                                setEditingUser(null);
                            }}
                            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                        >
                            Cancelar
                        </button>
                        
                        <button
                            onClick={handleSaveUser}
                            className="flex items-center px-6 py-2 bg-primary hover:bg-amber-400 text-white rounded-lg transition-colors duration-200"
                        >
                            <Icon name="save" className="w-4 h-4 mr-2" />
                            {editingUser ? 'Actualizar Usuario' : 'Crear Usuario'}
                        </button>
                    </div>
                </div>
            )}

            {/* Tabla de usuarios */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                            Lista de Usuarios
                        </h4>
                        
                        <div className="flex items-center space-x-3">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Buscar usuarios..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                                />
                                <Icon name="search" className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                            </div>
                            
                            <select
                                value={filterRole}
                                onChange={(e) => setFilterRole(e.target.value)}
                                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                                <option value="">Todos los roles</option>
                                <option value="superadmin">Super Admin</option>
                                <option value="admin">Admin</option>
                                <option value="user">Usuario</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Usuario
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Rol
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Departamento
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Estado
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Último Acceso
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredUsers.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-10 w-10">
                                                <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        {user.fullName.split(' ').map(n => n[0]).join('').toUpperCase()}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {user.fullName}
                                                </div>
                                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                                    {user.email}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                            user.role === 'superadmin' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                                            user.role === 'admin' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                                            'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                        }`}>
                                            {user.role === 'superadmin' ? 'Super Admin' :
                                             user.role === 'admin' ? 'Admin' : 'Usuario'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                        {user.department || '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                            user.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                            user.status === 'inactive' ? 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200' :
                                            'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                        }`}>
                                            {user.status === 'active' ? 'Activo' :
                                             user.status === 'inactive' ? 'Inactivo' : 'Suspendido'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                        {user.lastLogin || 'Nunca'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => handleEditUser(user)}
                                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                                title="Editar usuario"
                                            >
                                                <Icon name="edit" className="w-4 h-4" />
                                            </button>
                                            
                                            <button
                                                onClick={() => handleDeleteUser(user.id)}
                                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                                title="Eliminar usuario"
                                            >
                                                <Icon name="trash" className="w-4 h-4" />
                                            </button>
                                            
                                            <button
                                                onClick={() => handleToggleUserStatus(user.id)}
                                                className={`${user.status === 'active' ? 'text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300' : 'text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300'}`}
                                                title={user.status === 'active' ? 'Desactivar usuario' : 'Activar usuario'}
                                            >
                                                <Icon name={user.status === 'active' ? 'pause' : 'play'} className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Paginación */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                            Mostrando {((currentUserPage - 1) * usersPerPage) + 1} a {Math.min(currentUserPage * usersPerPage, filteredUsers.length)} de {filteredUsers.length} usuarios
                        </div>
                        
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => setCurrentUserPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentUserPage === 1}
                                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Anterior
                            </button>
                            
                            <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
                                Página {currentUserPage} de {Math.ceil(filteredUsers.length / usersPerPage)}
                            </span>
                            
                            <button
                                onClick={() => setCurrentUserPage(prev => Math.min(prev + 1, Math.ceil(filteredUsers.length / usersPerPage)))}
                                disabled={currentUserPage === Math.ceil(filteredUsers.length / usersPerPage)}
                                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Siguiente
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderContent = () => {
        switch (activeSection) {
            case 'equipos': return renderEquiposConfig();
            case 'centro_atencion': return renderCentroConfig();
            case 'usuarios_firebase': return renderUsuariosConfig();
            case 'llamadas': return <div className="text-center py-8 text-gray-500 dark:text-gray-400">Configuración de fuentes en desarrollo...</div>;
            default:
                return <div>Selecciona una sección</div>;
        }
    };

    const activeItem = menuItems.find(item => item.id === activeSection);

    return (
        <div className="flex h-full bg-white dark:bg-gray-900">
            {/* Sidebar de configuración */}
            <div className="w-auto lg:w-72 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
                <div className="p-2 lg:p-6 bg-gray-100 dark:bg-gray-800 h-full rounded-tl-xl">
                    <div className="flex items-center justify-center lg:justify-start mb-6">
                        <Settings className="h-8 w-8 text-primary dark:text-blue-400 mr-0 lg:mr-3" />
                        <div>
                            <h1 className="text-lg font-bold text-gray-900 dark:text-white hidden lg:block">
                                Configuración
                            </h1>
                            <p className="text-xs text-gray-500 dark:text-gray-400 hidden lg:block">
                                Panel de Administración
                            </p>
                        </div>
                    </div>

                    <nav className="space-y-2">
                        {menuItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = activeSection === item.id;

                            return (
                                <button
                                    key={item.id}
                                    onClick={() => setActiveSection(item.id)}
                                    className={`w-full flex items-center justify-between px-4 py-2 text-left rounded-lg transition-colors ${isActive
                                        ? `bg-primary dark:bg-primary/20 text-white dark:text-primary `
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                        }`}
                                >
                                    <div className="flex items-center sm:justify-start justify-center w-full">
                                        <Icon className={`h-5 w-5 lg:mr-3 ${isActive ? `text-white dark:text-primary` : 'text-gray-400 '}`} />
                                        <span className="font-medium text-sm hidden lg:block">{item.label}</span>
                                    </div>
                                </button>
                            );
                        })}
                    </nav>
                </div>
            </div>

            {/* Contenido principal */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Header */}
                <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            {activeItem && (
                                <>
                                    <activeItem.icon className={`h-6 w-6 text-${activeItem.color}-600 dark:text-${activeItem.color}-400 mr-3`} />
                                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                        {activeItem.label}
                                    </h2>
                                </>
                            )}
                        </div>

                        <div className="flex items-center space-x-3">
                            {saved && (
                                <div className="flex items-center text-green-600 dark:text-green-400">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span className="text-sm font-medium">Guardado</span>
                                </div>
                            )}

                            {/* <button
                                onClick={handleReset}
                                className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                            >
                                <RotateCcw className="h-4 w-4 mr-2" />
                                Restablecer
                            </button> */}

                            {/* <button
                                onClick={handleSave}
                                disabled={!isDirty}
                                className={`flex items-center px-4 py-2 text-sm rounded-md font-medium transition-colors ${isDirty
                                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                    : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                                    }`}
                            >
                                <Save className="h-4 w-4 mr-2" />
                                Guardar Cambios
                            </button> */}
                        </div>
                    </div>
                </div>

                {/* Área de contenido */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="max-w-4xl">
                        {renderContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Configuracion;